
import { User, HelpCircle, Bell } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { useBroadcastNotifications } from "@/hooks/useBroadcastNotifications";
import { useNavigateToSettingsTab } from "@/utils/navigateToSettingsTab";

export const Header = () => {
  const { user, signOut } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { unreadBroadcasts = [] } = useBroadcastNotifications();
  const goToMessagesTab = useNavigateToSettingsTab("messages");

  // Log pour le debug des notifications
  console.log('[Header]: Nombre de broadcasts non lus:', unreadBroadcasts?.length ?? 0);

  return (
    <div className="fixed top-0 left-0 w-full bg-white z-50 py-2 px-2 sm:p-4 shadow-md">
      <div className="px-0 sm:px-4 flex items-center justify-between gap-1 sm:gap-4 max-w-screen-2xl mx-auto">
        {isMobile && (
          <SidebarTrigger className="mr-1" />
        )}
        
        <Link to="/" className="flex items-center gap-1 sm:gap-3 hover:opacity-90 transition-opacity shrink-0 pl-0">
          <img 
            src="/lovable-uploads/e2b3cf9e-19de-4b54-9306-7b53664ced03.png" 
            alt="AlunaTools Logo" 
            className="h-8 w-auto sm:h-10" 
          />
          <span className="hidden sm:inline text-xl font-semibold font-nunito">
            AlunaTools
          </span>
        </Link>
        
        <div className="flex-1 flex flex-col justify-center items-center">
          {!user && (
            <div className="text-xs text-center text-blue-600">
              <Link to="/auth" className="hover:underline">
                Inscrivez-vous pour utiliser les outils sans limite, c'est gratuit !
              </Link>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 sm:gap-4 shrink-0">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/faq" className="hover:opacity-80 transition-opacity">
                  <HelpCircle className="h-5 w-5 sm:h-6 sm:w-6" />
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p>Aide et FAQ</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Bell icon for notifications */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="relative hover:opacity-80 transition-opacity cursor-pointer" onClick={goToMessagesTab}>
                  <Bell className="h-5 w-5 sm:h-6 sm:w-6" />
                  {user && Array.isArray(unreadBroadcasts) && unreadBroadcasts.length > 0 && (
                    <span className="absolute -top-1 -right-1">
                      <Badge variant="destructive" className="rounded-full w-3 h-3 p-0"></Badge>
                    </span>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Notifications</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <DropdownMenu>
            <DropdownMenuTrigger className="focus:outline-none">
              <div className="hover:opacity-80 transition-opacity rounded-full p-1">
                <User className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {user ? (
                <>
                  <DropdownMenuItem>
                    <Link to="/settings" className="w-full">
                      Paramètres
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={signOut}>
                    Déconnexion
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem>
                  <Link to="/auth" className="w-full flex items-center justify-between gap-2">
                    <span className="hidden sm:inline">Connexion</span>
                    <User className="h-5 w-5 sm:hidden" />
                  </Link>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
